import React, { useState, useEffect } from "react";
import {
  Table,
  Typography,
  Button,
  Breadcrumb,
  Card,
  Space,
  Input,
  Modal,
  Form,
  message,
  Tabs,
  Tag,
  Dropdown,
  Progress,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { TablePaginationConfig } from "antd/es/table";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import { useNavigate } from "react-router-dom";
import {
  HomeOutlined,
  SearchOutlined,
  UserOutlined,
  EditOutlined,
  UnlockOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import styled from "@emotion/styled";
import { supabase } from "../lib/supabase";
import { trackFeatureUsage } from "../lib/posthog";
import { AdminNavBar } from "../components/AdminNavBar";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const Container = styled.div`
  padding: 24px;
`;

const ActionButton = styled(Button)`
  margin-right: 8px;
`;

// Add compact styles for the table
const CompactTable = styled.div`
  .compact-table {
    .ant-table {
      font-size: 13px;
    }

    .ant-table-thead > tr > th {
      padding: 8px 16px;
    }

    .ant-table-tbody > tr > td {
      padding: 4px 16px;
    }

    .ant-table-cell {
      white-space: nowrap;
    }

    .ant-tag {
      margin-right: 0;
      font-size: 11px;
      padding: 0 4px;
      line-height: 18px;
    }

    .ant-btn {
      font-size: 12px;
      padding: 0 8px;
      height: 24px;
    }

    .ant-space {
      gap: 4px !important;
    }
  }
`;

// Define interface for our user type without extending the Supabase User type
interface AuthUser {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  user_metadata: {
    role?: string;
    first_name?: string;
    last_name?: string;
    email_verified?: boolean;
    phone_verified?: boolean;
  };
  app_metadata: {
    provider?: string;
    providers?: string[];
  };
  is_admin?: boolean;
  email_confirmed_at: string | null;
  banned: boolean;
  company_names: string[]; // Changed from optional to required with a default empty array
  companies?: Array<{
    id: string;
    company_id: string;
    user_id: string;
    created_at: string;
    company: {
      id: string;
      name: string;
      billing_email?: string;
      tax_id?: string;
      address?: string;
      city?: string;
      country?: string;
      active?: boolean;
      company_type?: string;
      created_at: string;
    };
  }>;
}

interface AdminUser {
  user_id: string;
  created_at: string;
  email?: string;
}

// Add pagination and server response interface
interface PaginationParams {
  page: number;
  pageSize: number;
  searchQuery?: string;
  sortField?: string;
  sortOrder?: "ascend" | "descend";
}

interface UsersResponse {
  users: AuthUser[];
  total: number;
  page: number;
  pageSize: number;
}

const UsersAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [adminLoading, setAdminLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AuthUser | null>(null);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState("1");

  // Add pagination and sorting state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<
    "ascend" | "descend" | undefined
  >();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  // Add state for password reset modal
  const [passwordResetModalVisible, setPasswordResetModalVisible] =
    useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [actionResult, setActionResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [actionModalVisible, setActionModalVisible] = useState(false);

  // Load state for companies
  const [loadingCompanies, setLoadingCompanies] = useState<{
    [key: string]: boolean;
  }>({});

  // Get the auth token for API requests
  const getAuthToken = async () => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token;
  };

  // Fetch users with their associated companies
  const fetchUsersWithCompanies = async (
    params?: Partial<PaginationParams>
  ) => {
    try {
      setLoading(true);

      const token = await getAuthToken();
      if (!token) {
        throw new Error("Not authenticated");
      }

      // Default values for pagination and sorting
      const page = params?.page || pagination.current;
      const pageSize = params?.pageSize || pagination.pageSize;
      const search = params?.searchQuery || searchQuery;
      const sort = params?.sortField || sortField;
      const order = params?.sortOrder || sortOrder;

      // Create query parameters
      const queryParams = new URLSearchParams();
      queryParams.append("page", page.toString());
      queryParams.append("pageSize", pageSize.toString());

      if (search) {
        queryParams.append("search", search);
      }

      if (sort) {
        queryParams.append("sortField", sort);
      }

      if (order) {
        queryParams.append("sortOrder", order);
      }

      // Call the Supabase Edge Function for admin users with query parameters
      const { data: usersData, error: usersError } =
        await supabase.functions.invoke(
          `admin-users/list?${queryParams.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

      if (usersError) throw usersError;

      // Ensure we have admin users data for marking
      if (adminUsers.length === 0) {
        await fetchAdminUsers();
      }

      // Extract pagination data and users
      const responseData = usersData as UsersResponse;

      // Add is_admin property to users
      const usersWithAdminStatus = responseData.users.map((user: AuthUser) => ({
        ...user,
        is_admin: adminUsers.some((admin) => admin.user_id === user.id),
        company_names: user.company_names || [],
      }));

      // Process users in batches to avoid performance issues
      const batchSize = 5;
      const userBatches = [];
      for (let i = 0; i < usersWithAdminStatus.length; i += batchSize) {
        userBatches.push(usersWithAdminStatus.slice(i, i + batchSize));
      }

      // Create a new loading state object for companies
      const newLoadingState: { [key: string]: boolean } = {};
      usersWithAdminStatus.forEach((user) => {
        newLoadingState[user.id] = true;
      });
      setLoadingCompanies(newLoadingState);

      // Process each batch
      for (const batch of userBatches) {
        await Promise.all(
          batch.map(async (user) => {
            try {
              // Try different methods to get the companies

              // 1. Try via admin service role API first (most likely to work)
              const companies = await fetchUserCompaniesViaAPI(user.id);
              if (companies.length > 0) {
                user.company_names = companies;
              } else {
                // 2. Try direct joins with better logging (fallback)
                const { data, error } = await supabase
                  .from("user_companies")
                  .select("*, company:company_id(name)")
                  .eq("user_id", user.id);

                if (error) {
                  console.error(
                    `Error in direct join for user ${user.id}:`,
                    error
                  );
                } else {
                  console.log(`Direct join result for user ${user.id}:`, data);

                  // If join worked
                  if (data && data.length > 0) {
                    user.company_names = data
                      .map((item: { company?: { name?: string } }) => {
                        if (item.company && typeof item.company === "object") {
                          return item.company.name;
                        }
                        return null;
                      })
                      .filter(
                        (name): name is string =>
                          name !== null && name !== undefined
                      );
                  } else {
                    // 3. Try two-step process (last resort)
                    console.log(`Trying two-step process for user ${user.id}`);
                    const { data: companyIds, error: idsError } = await supabase
                      .from("user_companies")
                      .select("company_id")
                      .eq("user_id", user.id);

                    if (idsError) {
                      console.error(
                        `Error fetching company IDs for user ${user.id}:`,
                        idsError
                      );
                    } else if (companyIds && companyIds.length > 0) {
                      console.log(
                        `Found company IDs for user ${user.id}:`,
                        companyIds
                      );
                      const ids = companyIds.map(
                        (item: { company_id: number }) => item.company_id
                      );

                      const { data: companyData, error: companiesError } =
                        await supabase
                          .from("company")
                          .select("name")
                          .in("id", ids);

                      if (companiesError) {
                        console.error(
                          `Error fetching company details for user ${user.id}:`,
                          companiesError
                        );
                      } else if (companyData && companyData.length > 0) {
                        console.log(
                          `Found company details for user ${user.id}:`,
                          companyData
                        );
                        user.company_names = companyData.map(
                          (c: { name: string }) => c.name
                        );
                      }
                    }
                  }
                }
              }

              // When done, update the loading state
              setLoadingCompanies((prev) => ({
                ...prev,
                [user.id]: false,
              }));

              if (user.company_names.length > 0) {
                // Only show success on the first company found to avoid too many messages
                console.log(
                  `Found ${user.company_names.length} companies for user ${user.email}`
                );
              }
            } catch (error) {
              console.error(
                `Error fetching companies for user ${user.id}:`,
                error
              );
              // Still mark as not loading on error
              setLoadingCompanies((prev) => ({
                ...prev,
                [user.id]: false,
              }));
            }
          })
        );
      }

      // Now all users should have their company_names populated
      setUsers(usersWithAdminStatus);

      // Debug user verification status
      console.log("User verification status:");
      usersWithAdminStatus.forEach((user) => {
        console.log(`User ${user.email}:`, {
          email_confirmed_at: user.email_confirmed_at,
          user_metadata_email_verified: user.user_metadata?.email_verified,
          verification_status:
            user.user_metadata?.email_verified === true &&
            user.email_confirmed_at !== null
              ? "Verified"
              : "Not Verified",
        });
      });

      // Update pagination state
      setPagination({
        current: responseData.page,
        pageSize: responseData.pageSize,
        total: responseData.total,
      });
    } catch (error) {
      console.error("Error fetching users:", error);
      message.error(
        "Failed to fetch users. Make sure you have admin privileges."
      );
    } finally {
      setLoading(false);
    }
  };

  // Replace existing fetchUsers function with the new one
  const fetchUsers = fetchUsersWithCompanies;

  // Fetch admin users
  const fetchAdminUsers = async () => {
    try {
      setAdminLoading(true);

      const token = await getAuthToken();
      if (!token) {
        throw new Error("Not authenticated");
      }

      // Call the Supabase Edge Function for admin users list
      const { data, error } = await supabase.functions.invoke(
        "admin-users/list-admins",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (error) throw error;

      // Get emails from auth.users for each admin
      if (data?.admins && data.admins.length > 0) {
        const adminsWithEmails = await Promise.all(
          data.admins.map(async (admin: AdminUser) => {
            try {
              // Call function to get user details
              const { data: userData, error: userError } =
                await supabase.functions.invoke("admin-users/get-user", {
                  method: "POST",
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                  body: {
                    userId: admin.user_id,
                  },
                });

              if (userError) throw userError;

              return {
                ...admin,
                email: userData?.user?.email || "Unknown",
              };
            } catch (err) {
              console.error("Error fetching user details:", err);
              return admin;
            }
          })
        );

        setAdminUsers(adminsWithEmails || []);
      } else {
        setAdminUsers(data?.admins || []);
      }
    } catch (error) {
      console.error("Error fetching admin users:", error);
      message.error("Failed to fetch admin users");
    } finally {
      setAdminLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "1") {
      fetchUsers();
    } else {
      fetchAdminUsers();
    }
    trackFeatureUsage("admin_users");
  }, [activeTab]);

  // Handle search with debounce
  const handleSearch = (value: string) => {
    setSearchText(value);

    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set a new timeout for debouncing
    const timeout = setTimeout(() => {
      setSearchQuery(value);
      fetchUsers({
        page: 1, // Reset to first page for new search
        searchQuery: value,
      });
    }, 500); // 500ms debounce

    setSearchTimeout(timeout);
  };

  // Handle table change for pagination, sorting
  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<AuthUser> | SorterResult<AuthUser>[]
  ) => {
    // Handle sorting
    let sortF: string | undefined;
    let sortO: "ascend" | "descend" | undefined;

    // Handle both single and multiple sorters
    if (!Array.isArray(sorter)) {
      if (sorter.field && sorter.order) {
        sortF = sorter.field as string;
        sortO = sorter.order;
      }
    }

    setSortField(sortF);
    setSortOrder(sortO);

    // Handle pagination - safely handle potentially undefined values
    fetchUsers({
      page: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
      sortField: sortF,
      sortOrder: sortO,
    });
  };

  // Edit user
  const handleEditUser = (user: AuthUser) => {
    setSelectedUser(user);
    form.setFieldsValue({
      email: user.email,
      first_name: user.user_metadata?.first_name || "",
      last_name: user.user_metadata?.last_name || "",
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!selectedUser) return;

      // Use Supabase Admin API directly
      const { error } = await supabase.auth.admin.updateUserById(
        selectedUser.id,
        {
          user_metadata: {
            ...selectedUser.user_metadata,
            first_name: values.first_name,
            last_name: values.last_name,
          },
        }
      );

      if (error) throw error;

      message.success("User updated successfully");
      fetchUsers();
      setEditModalVisible(false);
    } catch (error) {
      console.error("Error updating user:", error);
      message.error("Failed to update user");
    }
  };

  // Remove admin role from user
  const removeAdminRole = async (user: AuthUser) => {
    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error("Not authenticated");
      }

      // Call the Supabase Edge Function to remove admin role
      const { error } = await supabase.functions.invoke(
        "admin-users/remove-admin",
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: {
            userId: user.id,
          },
        }
      );

      if (error) throw error;

      message.success(`Admin privileges removed from ${user.email}`);
      fetchAdminUsers();
      fetchUsers();
    } catch (error) {
      console.error("Error removing admin role:", error);
      message.error("Failed to remove admin role");
    }
  };

  // Password reset handler
  const handlePasswordReset = async (user: AuthUser) => {
    setSelectedUser(user);
    setNewPassword("");
    setPasswordStrength(0);
    setPasswordResetModalVisible(true);
  };

  // Submit password reset
  const handlePasswordResetSubmit = async () => {
    if (!selectedUser) return;

    try {
      setConfirmLoading(true);

      // Use Supabase Admin API directly
      const { error } = await supabase.auth.admin.updateUserById(
        selectedUser.id,
        {
          password: newPassword,
        }
      );

      if (error) throw error;

      message.success("Password has been reset successfully");
      setPasswordResetModalVisible(false);
    } catch (error: unknown) {
      console.error("Error resetting password:", error);
      message.error("Failed to reset password");
    } finally {
      setConfirmLoading(false);
    }
  };

  // Check password strength
  const checkPasswordStrength = (password: string) => {
    // Simple password strength check
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;

    setPasswordStrength(strength);
    setNewPassword(password);
  };

  // Verify email handler
  const handleVerifyEmail = async (user: AuthUser) => {
    if (!user) return;

    try {
      setConfirmLoading(true);

      // Use Supabase Admin API directly to verify email
      const { error } = await supabase.auth.admin.updateUserById(user.id, {
        email_confirm: true,
        user_metadata: {
          ...user.user_metadata,
          email_verified: true,
        },
      });

      if (error) throw error;

      setActionResult({
        success: true,
        message: `Email ${user.email} has been verified successfully`,
      });
      setActionModalVisible(true);
      fetchUsers();
    } catch (error: unknown) {
      console.error("Error verifying email:", error);
      setActionResult({
        success: false,
        message: `Failed to verify email: ${
          error instanceof Error ? error.message : String(error)
        }`,
      });
      setActionModalVisible(true);
    } finally {
      setConfirmLoading(false);
    }
  };

  // Send password reset link handler
  const handleSendResetLink = async (user: AuthUser) => {
    if (!user) return;

    try {
      setConfirmLoading(true);

      const token = await getAuthToken();
      if (!token) {
        throw new Error("Not authenticated");
      }

      // Call the Supabase Edge Function to send reset link
      const { data, error } = await supabase.functions.invoke(
        "admin-users/generate-reset-link",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: {
            userId: user.id,
            redirectTo: `${window.location.origin}/auth/reset-password`,
          },
        }
      );

      if (error) throw error;

      setActionResult({
        success: true,
        message: `Password reset link generated: ${
          data?.link || "Link generated"
        }`,
      });
      setActionModalVisible(true);
    } catch (error: unknown) {
      console.error("Error generating reset link:", error);
      setActionResult({
        success: false,
        message: `Failed to generate reset link: ${
          error instanceof Error ? error.message : String(error)
        }`,
      });
      setActionModalVisible(true);
    } finally {
      setConfirmLoading(false);
    }
  };

  // Send verification link handler
  const handleSendVerificationLink = async (user: AuthUser) => {
    if (!user) return;

    try {
      setConfirmLoading(true);

      // Use Supabase Admin API directly to verify email
      const { error } = await supabase.auth.admin.updateUserById(user.id, {
        email_confirm: true,
        user_metadata: {
          ...user.user_metadata,
          email_verified: true,
        },
      });

      if (error) throw error;

      setActionResult({
        success: true,
        message: `Email ${user.email} has been verified successfully`,
      });
      setActionModalVisible(true);
      fetchUsers();
    } catch (error: unknown) {
      console.error("Error verifying email:", error);
      setActionResult({
        success: false,
        message: `Failed to verify email: ${
          error instanceof Error ? error.message : String(error)
        }`,
      });
      setActionModalVisible(true);
    } finally {
      setConfirmLoading(false);
    }
  };

  // Add a method to get companies using the admin API
  const fetchUserCompaniesViaAPI = async (userId: string) => {
    try {
      // Get the auth token which has admin privileges
      const token = await getAuthToken();
      if (!token) {
        throw new Error("Not authenticated");
      }

      // Call a custom Edge Function endpoint to get user companies
      const { data, error } = await supabase.functions.invoke(
        `admin-users/user-companies?userId=${userId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (error) {
        throw error;
      }

      console.log(`Companies for user ${userId} from API:`, data);

      // Map the response to extract company names
      const companies =
        data?.user_companies
          ?.map((uc: { company?: { name?: string } }) => uc.company?.name)
          .filter(Boolean) || [];

      return companies;
    } catch (error) {
      console.error(`Error fetching companies via API for ${userId}:`, error);
      return [];
    }
  };

  // Define columns with proper typing
  const columns: ColumnsType<AuthUser> = [
    {
      title: "First Name",
      key: "first_name",
      render: (_, record) => record.user_metadata?.first_name || "-",
      sorter: (a: AuthUser, b: AuthUser) =>
        (a.user_metadata?.first_name || "").localeCompare(
          b.user_metadata?.first_name || ""
        ),
    },
    {
      title: "Last Name",
      key: "last_name",
      render: (_, record) => record.user_metadata?.last_name || "-",
      sorter: (a: AuthUser, b: AuthUser) =>
        (a.user_metadata?.last_name || "").localeCompare(
          b.user_metadata?.last_name || ""
        ),
    },
    {
      title: "Name",
      dataIndex: "user_metadata",
      key: "name",
      render: (metadata) => {
        const firstName = metadata?.first_name || "";
        const lastName = metadata?.last_name || "";
        return `${firstName} ${lastName}`.trim() || "-";
      },
      sorter: true,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email, record) => (
        <Space>
          {email}
          {record.is_admin && <Tag color="blue">Admin</Tag>}
        </Space>
      ),
      sorter: (a: AuthUser, b: AuthUser) =>
        (a.email || "").localeCompare(b.email || ""),
    },
    {
      title: "Companies",
      dataIndex: "company_names",
      key: "companies",
      render: (companies: string[], record: AuthUser) => {
        // Show loading spinner if we're fetching companies for this user
        if (loadingCompanies[record.id]) {
          return (
            <span>
              Loading companies...{" "}
              <div className="ant-tag ant-tag-processing">⟳</div>
            </span>
          );
        }

        if (!companies || companies.length === 0) return "-";

        // If there are too many companies, show the first few with a count
        if (companies.length > 3) {
          return (
            <div>
              {companies.slice(0, 3).map((company, index) => (
                <Tag key={index} color="blue" style={{ marginBottom: "4px" }}>
                  {company}
                </Tag>
              ))}
              <Tag color="default">+{companies.length - 3} more</Tag>
            </div>
          );
        }

        // Otherwise show all companies
        return (
          <div>
            {companies.map((company, index) => (
              <Tag key={index} color="blue" style={{ marginBottom: "4px" }}>
                {company}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: "Role",
      key: "role",
      render: (_, record) => record.user_metadata?.role || "user",
      filters: [
        { text: "Admin", value: "admin" },
        { text: "User", value: "user" },
      ],
      onFilter: (value, record) =>
        (record.user_metadata?.role || "user") === value,
    },
    {
      title: "Provider",
      key: "provider",
      render: (_, record) => record.app_metadata?.provider || "email",
      filters: [
        { text: "Email", value: "email" },
        { text: "Google", value: "google" },
      ],
      onFilter: (value, record) =>
        (record.app_metadata?.provider || "email") === value,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => new Date(date).toLocaleDateString(),
      sorter: (a: AuthUser, b: AuthUser) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: "Last Sign In",
      dataIndex: "last_sign_in_at",
      key: "last_sign_in_at",
      render: (date: string | null) =>
        date ? new Date(date).toLocaleDateString() : "Never",
      sorter: (a: AuthUser, b: AuthUser) => {
        const dateA = a.last_sign_in_at
          ? new Date(a.last_sign_in_at).getTime()
          : 0;
        const dateB = b.last_sign_in_at
          ? new Date(b.last_sign_in_at).getTime()
          : 0;
        return dateA - dateB;
      },
    },
    {
      title: "Email Verified",
      key: "email_verified",
      width: 130,
      render: (_, record) => {
        // Check for verified email using both sources - require both to be true for verified status
        const isVerified =
          record.user_metadata?.email_verified === true &&
          record.email_confirmed_at !== null;

        return (
          <Tag color={isVerified ? "success" : "warning"}>
            {isVerified ? "Verified" : "Not Verified"}
          </Tag>
        );
      },
      filters: [
        { text: "Verified", value: true },
        { text: "Not Verified", value: false },
      ],
      onFilter: (value, record) => {
        const isVerified =
          record.user_metadata?.email_verified === true &&
          record.email_confirmed_at !== null;
        return isVerified === value;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: AuthUser) => (
        <Space wrap>
          <ActionButton
            icon={<EditOutlined />}
            onClick={() => handleEditUser(record)}
            title="Edit user details"
          />
          <Dropdown
            menu={{
              items: [
                {
                  key: "reset-password",
                  label: "Reset Password",
                  onClick: () => handlePasswordReset(record),
                },
                {
                  key: "verify-email",
                  label: "Verify Email",
                  onClick: () => handleVerifyEmail(record),
                  disabled: record.email_confirmed_at !== null,
                },
                {
                  key: "send-reset-link",
                  label: "Send Reset Link",
                  onClick: () => handleSendResetLink(record),
                },
                {
                  key: "send-verification",
                  label: "Force Verify Email",
                  onClick: () => handleSendVerificationLink(record),
                  disabled: record.email_confirmed_at !== null,
                },
              ],
            }}
          >
            <Button icon={<MoreOutlined />}>More Actions</Button>
          </Dropdown>
          {record.is_admin && (
            <ActionButton
              icon={<UnlockOutlined />}
              onClick={() => removeAdminRole(record)}
              title="Remove admin privileges"
            >
              Remove Admin
            </ActionButton>
          )}
        </Space>
      ),
    },
  ];

  // Admin users columns
  const adminColumns: ColumnsType<AdminUser> = [
    {
      title: "User ID",
      dataIndex: "user_id",
      key: "user_id",
    },
    {
      title: "Email",
      key: "email",
      dataIndex: "email",
      render: (email) => email || "Unknown",
    },
    {
      title: "Added At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => new Date(date).toLocaleDateString(),
      sorter: (a: AdminUser, b: AdminUser) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record: AdminUser) => {
        const user = users.find((u) => u.id === record.user_id) || {
          id: record.user_id,
        };
        return (
          <ActionButton
            icon={<UnlockOutlined />}
            danger
            onClick={() => removeAdminRole(user as AuthUser)}
            title="Remove admin privileges"
          >
            Remove Admin
          </ActionButton>
        );
      },
    },
  ];

  return (
    <Container>
      <AdminNavBar />

      <Title level={2}>User Management</Title>
      <Text type="secondary" style={{ marginBottom: 24, display: "block" }}>
        Manage user accounts, roles and permissions
      </Text>

      <CompactTable>
        <Card style={{ marginBottom: 24 }}>
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane tab="All Users" key="1">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 16,
                }}
              >
                <Input
                  placeholder="Search users by email, name, or ID"
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{ width: 300 }}
                  allowClear
                />
              </div>

              <Table
                dataSource={users}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                size="small"
                className="compact-table"
              />
            </TabPane>
            <TabPane tab="Admin Users" key="2">
              <Table
                dataSource={adminUsers}
                columns={adminColumns}
                rowKey="user_id"
                loading={adminLoading}
                pagination={{ pageSize: 20 }}
                size="small"
                className="compact-table"
              />
            </TabPane>
          </Tabs>
        </Card>
      </CompactTable>

      {/* Edit User Modal */}
      <Modal
        title="Edit User"
        open={editModalVisible}
        onOk={handleEditSubmit}
        onCancel={() => setEditModalVisible(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Email" name="email">
            <Input disabled prefix={<UserOutlined />} />
          </Form.Item>
          <Form.Item label="First Name" name="first_name">
            <Input />
          </Form.Item>
          <Form.Item label="Last Name" name="last_name">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete User"
        open={deleteModalVisible}
        onOk={() => {}}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to delete {selectedUser?.email}?</p>
        <p>
          This action cannot be undone. All user data will be permanently
          removed.
        </p>
      </Modal>

      {/* Password Reset Modal */}
      <Modal
        title="Reset User Password"
        open={passwordResetModalVisible}
        onOk={handlePasswordResetSubmit}
        confirmLoading={confirmLoading}
        onCancel={() => setPasswordResetModalVisible(false)}
      >
        <p>Set a new password for user: {selectedUser?.email}</p>
        <Form layout="vertical">
          <Form.Item
            label="New Password"
            required
            help={
              newPassword.length > 0
                ? `Password strength: ${
                    ["Very Weak", "Weak", "Fair", "Good", "Strong"][
                      passwordStrength
                    ]
                  }`
                : "Enter a new password"
            }
          >
            <Input.Password
              value={newPassword}
              onChange={(e) => checkPasswordStrength(e.target.value)}
              placeholder="Enter new password"
            />
          </Form.Item>
          {newPassword.length > 0 && (
            <Progress
              percent={passwordStrength * 20}
              status={
                passwordStrength < 2
                  ? "exception"
                  : passwordStrength < 4
                  ? "normal"
                  : "success"
              }
              showInfo={false}
            />
          )}
        </Form>
      </Modal>

      {/* Action Result Modal */}
      <Modal
        title={actionResult?.success ? "Success" : "Error"}
        open={actionModalVisible}
        onOk={() => setActionModalVisible(false)}
        onCancel={() => setActionModalVisible(false)}
        footer={[
          <Button
            key="copy"
            type="primary"
            onClick={() => {
              if (actionResult?.message.includes("http")) {
                const link =
                  actionResult.message.match(/(https?:\/\/[^\s]+)/g)?.[0];
                if (link) {
                  navigator.clipboard.writeText(link);
                  message.success("Link copied to clipboard");
                }
              }
            }}
          >
            Copy Link
          </Button>,
          <Button key="ok" onClick={() => setActionModalVisible(false)}>
            OK
          </Button>,
        ]}
      >
        <p>{actionResult?.message}</p>
      </Modal>
    </Container>
  );
};

export default UsersAdmin;
